/**
 * useColumnWidths — quản lý width per cột cho bảng dữ liệu (TableVirtuoso pattern).
 *
 * Mỗi bảng pass một `storageKey` duy nhất (vd "factory.bom.list"). Widths được
 * persist per-user qua backend (`user_preferences`, page_path='datatable',
 * preference_key='column_widths'). Cả app share một query duy nhất → chỉ
 * 1 request khi load app. Save có debounce 400ms sau khi thả chuột.
 *
 * Usage:
 *   const { getWidth, startResize } = useColumnWidths('factory.bom.list')
 *   <TableCell style={{ width: getWidth(col.key), position: 'relative' }}>
 *     {col.label}
 *     <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
 *   </TableCell>
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'

import {
  useGetUserTableWidthsQuery,
  usePutUserTableWidthsMutation,
  type ColumnWidths,
} from '@/store/api/userTableWidthsApi'

const MIN_COL_WIDTH = 50
const SAVE_DEBOUNCE_MS = 400

export interface UseColumnWidthsResult {
  widths: ColumnWidths
  /** Trả về width đã set, hoặc fallback nếu chưa resize. */
  getWidth: (key: string, fallback?: number) => number | undefined
  /** Set width cho 1 cột — auto-debounce save sang backend. */
  setWidth: (key: string, width: number) => void
  /** Bắt đầu drag resize — gọi từ onMouseDown của handle (TableVirtuoso pattern). */
  startResize: (key: string, event: ReactMouseEvent<HTMLElement>) => void
  /** Xoá width đã lưu → về default col.width (dùng cho nút "Default"). */
  resetWidths: () => void
}

export function useColumnWidths(storageKey?: string): UseColumnWidthsResult {
  const { data: allWidths } = useGetUserTableWidthsQuery(undefined, { skip: !storageKey })
  const [putWidths] = usePutUserTableWidthsMutation()

  const remoteWidths = useMemo<ColumnWidths>(
    () => (storageKey && allWidths?.[storageKey] ? allWidths[storageKey] : {}),
    [allWidths, storageKey],
  )

  const [localWidths, setLocalWidths] = useState<ColumnWidths>(remoteWidths)
  const lastRemoteRef = useRef<ColumnWidths>(remoteWidths)

  // Sync local state when remote widths change (initial load / external update).
  useEffect(() => {
    if (remoteWidths !== lastRemoteRef.current) {
      lastRemoteRef.current = remoteWidths
      setLocalWidths(remoteWidths)
    }
  }, [remoteWidths])

  const widthsRef = useRef(localWidths)
  widthsRef.current = localWidths

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleSave = useCallback(() => {
    if (!storageKey) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null
      putWidths({ tableKey: storageKey, widths: widthsRef.current }).catch(() => {
        // RTK Query already rolls back optimistic patch; nothing else to do.
      })
    }, SAVE_DEBOUNCE_MS)
  }, [storageKey, putWidths])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
    }
  }, [])

  const getWidth = useCallback(
    (key: string, fallback?: number) => localWidths[key] ?? fallback,
    [localWidths],
  )

  const setWidth = useCallback(
    (key: string, width: number) => {
      const clamped = Math.max(MIN_COL_WIDTH, Math.round(width))
      setLocalWidths((prev) => (prev[key] === clamped ? prev : { ...prev, [key]: clamped }))
      scheduleSave()
    },
    [scheduleSave],
  )

  const dragRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null)

  const startResize = useCallback(
    (key: string, event: ReactMouseEvent<HTMLElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const handle = event.currentTarget as HTMLElement
      const cell = handle.closest('th, td') as HTMLElement | null
      const startWidth = cell?.getBoundingClientRect().width ?? localWidths[key] ?? 100
      const startX = event.clientX
      dragRef.current = { key, startX, startWidth }

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const onMove = (e: MouseEvent) => {
        if (!dragRef.current) return
        const delta = e.clientX - dragRef.current.startX
        const next = Math.max(MIN_COL_WIDTH, Math.round(dragRef.current.startWidth + delta))
        setLocalWidths((prev) => (prev[dragRef.current!.key] === next ? prev : { ...prev, [dragRef.current!.key]: next }))
      }
      const onUp = () => {
        dragRef.current = null
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        scheduleSave()
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [localWidths, scheduleSave],
  )

  // Reset về default: xoá toàn bộ width đã lưu cho storageKey → cột dùng lại
  // col.width khai báo trong code (dùng cho nút "Default").
  const resetWidths = useCallback(() => {
    setLocalWidths({})
    if (storageKey) putWidths({ tableKey: storageKey, widths: {} }).catch(() => undefined)
  }, [storageKey, putWidths])

  return { widths: localWidths, getWidth, setWidth, startResize, resetWidths }
}
